import { ensureGetList } from '@/requests/hooks/use-get-list';
import { tryCatch } from '@/utils/try-catch';
import type {
  Company,
  ExternalClient,
  ModelName,
  RealEstate,
  Segment,
} from '@pigello/pigello-matrix';
import type { GlobalFilter, GlobalFilterItem } from './types';

type FilterModel = RealEstate | Segment | ExternalClient | Company;

interface FilterTask {
  model: ModelName;
  ids: string[];
  type: GlobalFilterItem['type'];
}

export async function getGlobalFilterItems(
  filter: GlobalFilter[string]
): Promise<GlobalFilterItem[]> {
  const {
    global_realestates,
    global_segments,
    filter_external_clients,
    global_companies,
  } = filter;

  // Create a list of tasks only for categories with non-empty input arrays
  const tasks: FilterTask[] = [];

  if (global_realestates.length > 0) {
    tasks.push({
      model: 'realestate',
      ids: global_realestates,
      type: 'global_realestates',
    });
  }

  if (global_segments.length > 0) {
    tasks.push({
      model: 'segment',
      ids: global_segments,
      type: 'global_segments',
    });
  }

  if (filter_external_clients.length > 0) {
    tasks.push({
      model: 'externalclient',
      ids: filter_external_clients,
      type: 'filter_external_clients',
    });
  }

  if (global_companies.length > 0) {
    tasks.push({
      model: 'company',
      ids: global_companies,
      type: 'global_companies',
    });
  }

  // Map each task to a promise calling getGlobalFilterItemsForModel
  const promises = tasks.map((task) =>
    getGlobalFilterItemsForModel(task.model, task.ids)
  );

  // Run all promises in parallel and collect results
  const results = await Promise.all(promises);

  // Iterate the resolved results alongside the original task descriptors
  const globalFilter: GlobalFilterItem[] = [];
  for (let i = 0; i < results.length; i++) {
    const task = tasks[i];
    const items = results[i];

    for (const item of items) {
      globalFilter.push({
        id: item.id,
        type: task.type,
        displayString: item.name,
      });
    }
  }

  return globalFilter;
}

async function getGlobalFilterItemsForModel<T extends FilterModel>(
  modelName: ModelName,
  ids: string[]
): Promise<T[]> {
  const { data, error } = await tryCatch(async () => {
    const res = await ensureGetList<T>({
      modelName,
      queryParams: {
        filters: { id: { __in: ids.join(',') } },
      },
    });
    return res?.list ?? [];
  });
  if (error) {
    return [];
  }
  return data;
}

export function computeToggledFilteredItems(
  prevItems: GlobalFilterItem[],
  item: GlobalFilterItem
): { items: GlobalFilterItem[]; shouldToast: boolean } {
  // If item already exists, remove it
  if (prevItems.find((p) => p.id === item.id && p.type === item.type)) {
    return {
      items: prevItems.filter(
        (p) => !(p.id === item.id && p.type === item.type)
      ),
      shouldToast: false,
    };
  }

  // If adding a segment, keep only segments
  if (item.type === 'global_segments') {
    const prevWithSegmentsOnly = prevItems.filter(
      (p) => p.type === 'global_segments'
    );
    return {
      items: [...prevWithSegmentsOnly, item],
      shouldToast: prevWithSegmentsOnly.length < prevItems.length,
    };
  }

  // If adding non-segment but segments exist, clear segments
  if (prevItems.find((p) => p.type === 'global_segments')) {
    const prevWithoutSegments = prevItems.filter(
      (p) => p.type !== 'global_segments'
    );
    return {
      items: [...prevWithoutSegments, item],
      shouldToast: true,
    };
  }

  // Default: just add the item
  return {
    items: [...prevItems, item],
    shouldToast: false,
  };
}
